import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { SKILLS } from '../game-system/skills/skillSystem';
import { SKILL_RANKS, canRankUpSkill } from '../game-system/skills/skillProgression';
import SkillRankUpDialog from './SkillRankUpDialog';
import './CharacterSkills.css';

/**
 * Component for displaying and managing character skills
 */
const CharacterSkills = ({ character, onCharacterUpdate }) => {
  const [skillsByCategory, setSkillsByCategory] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [rankupDialogOpen, setRankupDialogOpen] = useState(false);
  const [rankableSkills, setRankableSkills] = useState([]);
  
  // Group skills by category and check for rankable skills
  useEffect(() => {
    if (!character || !character.skills) return;
    
    // Group all skills by category
    const categories = {};
    
    // Add all available skills, even if character doesn't have them yet
    Object.entries(SKILLS).forEach(([key, skill]) => {
      const category = skill.category || 'other';
      
      if (!categories[category]) {
        categories[category] = [];
      }
      
      // Find the character's version of this skill if it exists
      const characterSkill = character.skills.find(s => 
        s.skill.name.toLowerCase() === skill.name.toLowerCase()
      );
      
      // Use character's skill data or create a default entry
      const skillData = characterSkill || {
        skill: {
          name: skill.name,
          ...skill
        },
        level: 0,
        experience: 0
      };
      
      // Add extra info from the SKILLS definition
      skillData.skill = {
        ...skillData.skill,
        attribute: skill.attribute,
        description: skill.description,
        category: skill.category,
        usedFor: skill.usedFor,
        trainedOnly: skill.trainedOnly
      };
      
      categories[category].push(skillData);
    });
    
    setSkillsByCategory(categories);
    
    // Check for skills eligible for rank up
    const rankable = character.skills ? character.skills.filter(
      skill => canRankUpSkill(character, skill.skill.name)
    ) : [];
    
    setRankableSkills(rankable);
  }, [character]);
  
  // Format skill rank display
  const formatRank = (level) => {
    return SKILL_RANKS[level]?.name || 'Unknown';
  };
  
  // Filter skills based on active category
  const getFilteredSkills = () => {
    if (activeCategory === 'all') {
      return Object.values(skillsByCategory).flat();
    }
    
    return skillsByCategory[activeCategory] || [];
  };
  
  // Sort skills by name
  const getSortedSkills = (skills) => {
    return [...skills].sort((a, b) => {
      // Sort by level (descending) first
      if (b.level !== a.level) {
        return b.level - a.level;
      }
      // Then by experience (descending)
      if (b.experience !== a.experience) {
        return b.experience - a.experience;
      }
      // Finally by name (ascending)
      return a.skill.name.localeCompare(b.skill.name);
    });
  };
  
  // Get categories with skill counts
  const getCategoriesWithCounts = () => {
    const categories = Object.keys(skillsByCategory);
    const result = categories.map(category => ({
      name: category,
      count: skillsByCategory[category].filter(s => s.level > 0).length,
      total: skillsByCategory[category].length
    }));
    
    // Add "all" category
    const totalTrained = Object.values(skillsByCategory)
      .flat()
      .filter(s => s.level > 0)
      .length;
    
    const totalSkills = Object.values(skillsByCategory).flat().length;
    
    return [
      { name: 'all', count: totalTrained, total: totalSkills },
      ...result
    ];
  };
  
  // Get XP progress percentage for next rank
  const getXpProgressPercentage = (skill) => {
    if (!skill) return 0;
    
    const nextRank = skill.level + 1;
    
    // If already at max rank
    if (nextRank >= Object.keys(SKILL_RANKS).length) {
      return 100;
    }
    
    const requiredXP = nextRank * nextRank * 100; // From skillProgression.js
    return Math.min(100, Math.floor((skill.experience / requiredXP) * 100));
  };
  
  // Get formatted used skill list
  const getUsedForList = (usedFor) => {
    if (!usedFor || !usedFor.length) return 'Various tasks';
    
    return usedFor.join(', ');
  };
  
  // Handle rank up result
  const handleRankUp = (result) => {
    // Already handled by onCharacterUpdate, but could add notifications or other effects here
  };
  
  // Render rank up button if skills can be ranked up
  const renderRankUpButton = () => {
    if (rankableSkills.length === 0) return null;
    
    return (
      <Button 
        onClick={() => setRankupDialogOpen(true)}
        className="rank-up-button"
        variant="default"
      >
        Rank Up Available ({rankableSkills.length})
      </Button>
    );
  };
  
  return (
    <div className="character-skills-container">
      {/* Category Tabs */}
      <div className="skill-categories">
        {getCategoriesWithCounts().map(category => (
          <button
            key={category.name}
            className={`category-tab ${activeCategory === category.name ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.name)}
          >
            {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
            <span className="category-count">
              {category.count}/{category.total}
            </span>
          </button>
        ))}
      </div>
      
      {/* Rank Up Button */}
      {renderRankUpButton()}
      
      {/* Skills List */}
      <Card className="skills-card">
        <CardHeader>
          <CardTitle>
            {activeCategory === 'all' 
              ? 'All Skills' 
              : `${activeCategory.charAt(0).toUpperCase()}${activeCategory.slice(1)} Skills`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="skills-list">
            {getSortedSkills(getFilteredSkills()).map((skill, index) => (
              <div 
                key={`${skill.skill.name}-${index}`}
                className={`skill-item ${skill.level > 0 ? 'trained' : 'untrained'} ${
                  canRankUpSkill(character, skill.skill.name) ? 'can-rank-up' : ''
                }`}
              >
                <div className="skill-header">
                  <div className="skill-name-row">
                    <h3 className="skill-name">{skill.skill.name}</h3>
                    <span className="skill-rank">{formatRank(skill.level)}</span>
                  </div>
                  <div className="skill-attribute">
                    {skill.skill.attribute.charAt(0).toUpperCase() + skill.skill.attribute.slice(1)}
                    {skill.skill.trainedOnly && <span className="trained-only"> (Training Required)</span>}
                  </div>
                </div>
                
                <div className="skill-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${getXpProgressPercentage(skill)}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {skill.experience} XP
                    {canRankUpSkill(character, skill.skill.name) && ' - Ready to Advance!'}
                  </span>
                </div>
                
                <p className="skill-description">{skill.skill.description}</p>
                
                <div className="skill-uses">
                  <strong>Used for:</strong> {getUsedForList(skill.skill.usedFor)}
                </div>
              </div>
            ))}
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Rank Up Dialog */}
      <SkillRankUpDialog
        character={character}
        open={rankupDialogOpen}
        onOpenChange={setRankupDialogOpen}
        onCharacterUpdate={onCharacterUpdate}
        onRankUp={handleRankUp}
      />
    </div>
  );
};

export default CharacterSkills;